import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SITE } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay }) {
    return (
        <span className="block overflow-hidden pb-[0.08em]">
            <motion.span
                className="block will-change-transform"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const portraitY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const typeY = useTransform(scrollYProgress, [0, 1], [0, -50]);

    return (
        <section
            id="home"
            ref={ref}
            className="relative flex min-h-screen flex-col items-center justify-center px-6 pb-16 pt-28 sm:px-10"
        >
            <motion.div
                style={{ y: portraitY }}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                className="relative"
            >
                <div
                    className="h-36 w-36 overflow-hidden rounded-full border-2 border-[#E5E1D8] p-1 shadow-md sm:h-44 sm:w-44"
                    data-testid="hero-portrait-image"
                >
                    <img
                        src={SITE.portrait}
                        alt={SITE.portraitAlt}
                        style={{ objectPosition: SITE.portraitPos }}
                        className="h-full w-full rounded-full object-cover grayscale transition-[filter] duration-700 hover:grayscale-0"
                    />
                </div>
                <span
                    className="pulse-dot absolute bottom-3 right-3 h-3.5 w-3.5 rounded-full border-2 border-[#FBF9F5] bg-[#C84B31]"
                    title={SITE.location}
                />
            </motion.div>

            <motion.h1
                style={{ y: typeY }}
                data-testid="hero-headline"
                className="mt-10 max-w-3xl text-center font-serif text-4xl font-normal leading-[1.12] tracking-tight text-[#141413] sm:text-5xl md:text-6xl"
            >
                <MaskedLine delay={0.3}>Hello, I'm Vinayak.</MaskedLine>
                <MaskedLine delay={0.42}>I work across investments,</MaskedLine>
                <MaskedLine delay={0.54}>
                    real estate and strategy —
                </MaskedLine>
                <MaskedLine delay={0.66}>
                    <span className="italic text-[#C84B31]">
                        long-term by design.
                    </span>
                </MaskedLine>
            </motion.h1>

            <motion.p
                data-testid="hero-subheadline"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
                className="mt-8 font-mono text-[11px] uppercase tracking-[0.22em] text-[#8C8B88] sm:text-xs"
            >
                MBA, Indian School of Business — Buildtech &amp; Banking
            </motion.p>

            <motion.a
                href="#bio"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 1.3 }}
                className="absolute bottom-8 flex flex-col items-center gap-2 text-[#8C8B88] transition-colors duration-300 hover:text-[#141413]"
                aria-label="Scroll to about"
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.3em]">
                    scroll
                </span>
                <motion.span
                    animate={{ scaleY: [1, 0.4, 1], opacity: [1, 0.4, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="block h-8 w-px origin-top bg-current"
                />
            </motion.a>
        </section>
    );
}
